/**
 * When an order arrives, as the customer reads it: dates with the price difference, never the Speed's name
 * (Economy, Standard, Express: Brad, 22 Oct). The dates and the prices are the server's (`CustomerSpeedSchema`);
 * this only words them, in the portal's date style ("Fri 28 Oct").
 *
 * A window ("Tue 25 – Fri 28 Oct") is two dates, the earliest it could arrive (before the supplier's buffer) and the
 * date it is promised (with it). Equal dates read as one date ("Arrives Fri 28 Oct"); any gap, a day included, reads
 * as a window (Brad, 2 Nov). Both come from the server; nothing here counts days.
 */

import type { CustomerSpeedChoice } from "../data/customer-api-types";
import { ApiError } from "../data/api/errors";
import { extractFieldErrors } from "../data/validation";
import {
  formatCalendarWeekdayDateOrDash,
  formatQuotedPrice,
} from "./formatters";

/** What a Speed that a person must confirm reads in place of a date (the server's own words, d67). */
export const CHECK_WITH_OUR_TEAM = "Check with our team";

/**
 * "Fri 28 Oct" for one date; "Tue 25 – Fri 28 Oct" for a window in one month, "Tue 30 Oct – Fri 2 Nov" across two.
 * The earliest date is optional: none, or the same day as the latest, is one date. "-" for an unreadable date.
 */
export function formatArrival(
  latestOn: string,
  earliestOn?: string | null,
): string {
  const latest = formatCalendarWeekdayDateOrDash(latestOn);
  if (!earliestOn || earliestOn === latestOn) return latest;
  const earliest = formatCalendarWeekdayDateOrDash(earliestOn);
  if (earliest === "-" || latest === "-") return latest;
  const [earliestWeekday, earliestDay, earliestMonth] = earliest.split(" ");
  const latestMonth = latest.split(" ")[2];
  const start =
    earliestMonth === latestMonth
      ? `${earliestWeekday} ${earliestDay}`
      : earliest;
  return `${start} – ${latest}`;
}

/** The line's arrival in a sentence: "Arrives Fri 28 Oct", or "Expected Tue 25 – Fri 28 Oct" for a window. */
export function arrivalSentence(
  latestOn: string,
  earliestOn?: string | null,
): string | null {
  const when = formatArrival(latestOn, earliestOn);
  if (when === "-") return null;
  return earliestOn && earliestOn !== latestOn
    ? `Expected ${when}`
    : `Arrives ${when}`;
}

/** What a basket line says where an address has no date of its own to give. */
export const ADDRESS_DATE_TO_CONFIRM = "We'll confirm the date";

/** An address's dates as the basket read carries them. */
export interface AddressArrival {
  expected_arrival_on?: string | null;
  expected_arrival_from_on?: string | null;
}

/**
 * What each address of a line says about when it arrives, or null while there is nothing to say per address: one
 * address, or addresses that all arrive together (or none has a date), where the line's own date says it once.
 * Where they differ, each address says its own ("Arrives Thu 27 Oct", "Expected Thu 27 – Fri 28 Oct"), and an
 * address with no date says it is to be confirmed, never a guess. A date already past arrives as null from the
 * server, so it is never worded here.
 */
export function addressArrivalSentences(
  addresses: readonly AddressArrival[],
): string[] | null {
  if (addresses.length < 2) return null;
  const keyOf = (a: AddressArrival) =>
    `${a.expected_arrival_from_on ?? ""}|${a.expected_arrival_on ?? ""}`;
  const [first] = addresses;
  if (first && addresses.every((a) => keyOf(a) === keyOf(first))) return null;
  return addresses.map(
    (a) =>
      (a.expected_arrival_on
        ? arrivalSentence(a.expected_arrival_on, a.expected_arrival_from_on)
        : null) ?? ADDRESS_DATE_TO_CONFIRM,
  );
}

/** One way of getting an item, as a date and what it costs more than the cheapest way. */
export interface DeliveryDateOption {
  /** What is sent back with the price and the add to basket; never shown. */
  speedCode: string;
  /** "Fri 28 Oct", "Thu 27 – Fri 28 Oct" for a window, or "Check with our team" where a person must confirm it. */
  when: string;
  /** `when` is a window (two different dates), which reads "Expected …" in a sentence. */
  isWindow: boolean;
  /** The server's estimate before the address is known: shown as "usually". */
  isEstimate: boolean;
  isToBeConfirmed: boolean;
  /** Ex VAT, for the whole line. */
  pricePence: number;
  /** More than the cheapest option, ex VAT; 0 for the cheapest and any that cost the same. */
  extraPence: number;
  /** "+£12.00", or "No extra cost" for 0. */
  extraLabel: string;
  isSelected: boolean;
}

/** What the options need of a Speed, as the item page's Speeds and a basket line's preview both carry it. */
export interface SpeedFacts {
  speed_code: string;
  /** Ex VAT, for the whole line. */
  price_pence: number;
  arrives_on?: string | null;
  arrives_earliest_on?: string | null;
  is_estimate: boolean;
  is_to_be_confirmed: boolean;
}

/** The options the customer picks from, in the server's order (slowest, cheapest first); empty with no choice to make. */
export function deliveryDateOptions(
  choice: CustomerSpeedChoice | null | undefined,
): DeliveryDateOption[] {
  if (!choice) return [];
  return deliveryDateOptionsOf(
    choice.speeds.map((speed) => ({
      ...speed,
      price_pence: speed.price.price_pence,
    })),
    choice.selected_code,
  );
}

/** The same options from Speeds that carry their price flat (a basket line's preview), `selectedCode` the one chosen. */
export function deliveryDateOptionsOf(
  speeds: readonly SpeedFacts[],
  selectedCode: string | null | undefined,
): DeliveryDateOption[] {
  if (speeds.length === 0) return [];
  const cheapest = Math.min(...speeds.map((s) => s.price_pence));
  return speeds.map((speed) => optionOf(speed, selectedCode, cheapest));
}

function optionOf(
  speed: SpeedFacts,
  selectedCode: string | null | undefined,
  cheapestPence: number,
): DeliveryDateOption {
  const extraPence = speed.price_pence - cheapestPence;
  const arrival =
    !speed.is_to_be_confirmed && speed.arrives_on
      ? formatArrival(speed.arrives_on, speed.arrives_earliest_on)
      : "-";
  const isToBeConfirmed = arrival === "-";
  return {
    speedCode: speed.speed_code,
    when: isToBeConfirmed ? CHECK_WITH_OUR_TEAM : arrival,
    isWindow:
      !isToBeConfirmed &&
      !!speed.arrives_earliest_on &&
      speed.arrives_earliest_on !== speed.arrives_on,
    isEstimate: speed.is_estimate && !isToBeConfirmed,
    isToBeConfirmed,
    pricePence: speed.price_pence,
    extraPence,
    extraLabel:
      extraPence > 0 ? `+${formatQuotedPrice(extraPence)}` : "No extra cost",
    isSelected: speed.speed_code === selectedCode,
  };
}

/**
 * Asks for a price at the date the customer chose. A date the item stops offering after an option or quantity
 * change comes back refused on `speed_code`; that refusal is not for the customer to read (its message names the
 * Speeds), so the price is asked for again at the item's own default; the answer's `selected_code` says which date
 * it is at.
 */
export async function askAtChosenSpeed<T>(
  ask: (speedCode: string | null) => Promise<T>,
  chosenSpeedCode: string | null,
): Promise<T> {
  if (chosenSpeedCode === null) return ask(null);
  try {
    return await ask(chosenSpeedCode);
  } catch (err) {
    if (isSpeedNotOffered(err)) return ask(null);
    throw err;
  }
}

function isSpeedNotOffered(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    extractFieldErrors(err).some(
      (e) => e.field === "speed_code" && e.code === "speed_not_offered",
    )
  );
}
